import { Link } from 'react-router-dom';
import { skipToken } from '@reduxjs/toolkit/query';
import { useAppSelector } from '@/app/hooks';
import { selectCurrentUser } from '@mmef/shared/auth';
import { DismissButton } from '@/components/DismissButton';
import { useDismissed } from '@/components/useDismissed';
import { useListSubscriptionsQuery } from './subscriptionsApi';

const ALERTS_PATH = '/account/alerts';

/**
 * Nudge above the search towards setting up an alert. Left half of the pair.
 *
 * <p>Alerts were only reachable from inside the account area, which meant the one feature that
 * brings people back was invisible to anyone who hadn't gone looking for it.
 *
 * <p>Disappears in two ways, and the CSS handles both: hidden once someone already has alerts —
 * a standing prompt to do something you've done is just clutter — and closable for anyone who
 * simply doesn't want it. Either way {@code .promo-pair} gives the remaining card the full row.
 */
export function AlertPrompt() {
  const user = useAppSelector(selectCurrentUser);
  const { data: subscriptions } = useListSubscriptionsQuery(user ? undefined : skipToken);
  const [dismissed, dismiss] = useDismissed('alert-prompt');

  if (dismissed) return null;
  if (user && subscriptions && subscriptions.length > 0) return null;

  return (
    <aside className="promo">
      <div className="promo__body">
        <p className="promo__title">Never miss a new event</p>
        <p className="promo__text">We&apos;ll email you when one matches.</p>
      </div>

      {user ? (
        <Link className="button button--primary" to={ALERTS_PATH}>
          Set up an alert
        </Link>
      ) : (
        // Carries the destination, so signing in lands them on the alerts page rather than back
        // here wondering what happened to the thing they clicked.
        <Link
          className="button button--primary"
          to="/sign-in"
          state={{ from: { pathname: ALERTS_PATH } }}
        >
          Set up an alert
        </Link>
      )}

      <DismissButton label="Dismiss the alerts prompt" onDismiss={dismiss} />
    </aside>
  );
}
